import { useState, useCallback, type FC } from "react";

// ── Evaluation result types ──
export type EvalLevel = "rojo" | "amarillo" | "verde";

export interface EvaluatedFragment {
  text: string;
  level: EvalLevel;
  suggestion: string;
  reason: string;
}

export interface EvaluationResult {
  fragments: EvaluatedFragment[];
  overallLevel: EvalLevel;
  summary: string;
}

// ── Rule engine ──
interface Rule {
  pattern: RegExp;
  level: EvalLevel;
  reason: string;
  suggestionTemplate: string;
}

const RED_RULES: Rule[] = [
  {
    pattern: /\b(es|son)\s+(un|una)\s+(irresponsable|flojo|conflictivo|agresivo|mentiroso|problem[aá]tico|desordenado|malcriado|desinteresado|manipulador|violento|desubicado|sinverg[üu]enza)\b/gi,
    level: "rojo",
    reason: "Juicio de valor sobre el estudiante. Reemplace por conductas observables específicas.",
    suggestionTemplate: "El estudiante [describe conducta observable] durante [periodo] en [contexto].",
  },
  {
    pattern: /\b(la mam[aá]|el pap[aá]|el apoderado|la apoderada|la familia)\s+(nunca|jam[aá]s)\s+(apoya|viene|asiste|colabora|responde|se preocupa|hace caso)\b/gi,
    level: "rojo",
    reason: "Afirmación absoluta sobre el apoderado sin registro de respaldo. Use datos de contacto registrados.",
    suggestionTemplate: "No se registra [asistencia/respuesta] del apoderado a las [citaciones/reuniones] realizadas los días [fechas].",
  },
  {
    pattern: /\b(no\s+tiene\s+soluci[oó]n|es\s+un\s+caso\s+perdido|no\s+hay\s+nada\s+que\s+hacer|no\s+vale\s+la\s+pena)\b/gi,
    level: "rojo",
    reason: "Expresión derrotista y no profesional. Mantenga un tono propositivo y centrado en datos.",
    suggestionTemplate: "Se han aplicado las siguientes medidas: [detallar]. Se sugiere evaluar [medidas adicionales].",
  },
  {
    pattern: /\b(tiene\s+(un\s+)?(trastorno|d[eé]ficit|TDAH|TEA|depresi[oó]n|ansiedad|bipolar|esquizofrenia|psicosis)|es\s+(autista|hiperactivo|depresivo|ansioso|bipolar|disl[eé]xico|psic[oó]tico))\b/gi,
    level: "rojo",
    reason: "Diagnóstico clínico no respaldado. Solo profesionales de salud mental pueden emitir diagnósticos.",
    suggestionTemplate: "Se observa que el estudiante [describe conducta observable sin etiquetar].",
  },
  {
    pattern: /\b(la culpa es de|es culpa de|los responsables son|la familia tiene la culpa)\b/gi,
    level: "rojo",
    reason: "Atribución de culpa. Mantenga un lenguaje descriptivo y neutral.",
    suggestionTemplate: "Respecto a [situación], se registra que [hecho objetivo].",
  },
  {
    pattern: /\b(yo creo que|en mi opini[oó]n|me parece que|pienso que|siento que)\s+(es|est[aá])\b/gi,
    level: "rojo",
    reason: "Opinión personal sin respaldo en datos observables. Use registros concretos.",
    suggestionTemplate: "Según registros disponibles, [dato objetivo].",
  },
  {
    pattern: /\b(siempre\s+(se porta mal|interrumpe|molesta|pelea|llega tarde|falta)|nunca\s+(hace caso|obedece|trabaja|participa|estudia))\b/gi,
    level: "rojo",
    reason: "Generalización absoluta. Especifique frecuencia y contexto.",
    suggestionTemplate: "Durante [periodo], se registraron [cantidad] de [conductas/incidentes/situaciones].",
  },
];

const YELLOW_RULES: Rule[] = [
  {
    pattern: /\b(se porta\s+(mal|regular|bien)|tiene\s+(mal|buen)\s+comportamiento)\b/gi,
    level: "amarillo",
    reason: "Frase ambigua. Especifique qué conductas observables ocurren.",
    suggestionTemplate: "Especificar conducta: interrumpe la clase, se levanta sin permiso, responde de forma inadecuada, no sigue instrucciones, etc.",
  },
  {
    pattern: /\b(le\s+cuesta|cuesta\s+mucho|le\s+va\s+mal|anda\s+mal)\b/gi,
    level: "amarillo",
    reason: "Imprecisión. Indique áreas específicas y evidencia concreta.",
    suggestionTemplate: "En [asignatura/área], el estudiante [describe desempeño con datos: notas, % de cumplimiento].",
  },
  {
    pattern: /\b(a\s+veces|de\s+repente|de\s+vez\s+en\s+cuando|ocasionalmente)\b/gi,
    level: "amarillo",
    reason: "Expresión vaga. Cuantifique: ¿cuántas veces? ¿en qué periodo?",
    suggestionTemplate: "Durante [periodo específico], se observó [conducta] en [cantidad] ocasiones.",
  },
  {
    pattern: /\b(no\s+participa\s+mucho|participa\s+poco|casi\s+no\s+participa)\b/gi,
    level: "amarillo",
    reason: "Frase ambigua. Describa el tipo de participación esperada y la observada.",
    suggestionTemplate: "Se registra participación en [X] de [Y] clases durante [periodo]. [Describir tipo de participación o falta de ella].",
  },
  {
    pattern: /\b(mejor[oó]\s+(un\s+poco|algo|bastante)|ha\s+mejorado)\b/gi,
    level: "amarillo",
    reason: "Afirmación sin parámetros comparativos. Use datos de antes y después.",
    suggestionTemplate: "En comparación con [periodo anterior], [indicador] pasó de [valor A] a [valor B].",
  },
  {
    pattern: /\b(tiene\s+problemas\s+(para|de|con)|presenta\s+dificultades)\b/gi,
    level: "amarillo",
    reason: "Descripción general. Especifique qué tipo de dificultades y con qué evidencias.",
    suggestionTemplate: "Especificar: ¿Dificultades en qué área? ¿Con qué frecuencia? ¿Qué evidencias las respaldan?",
  },
];

const GREEN_PATTERNS: RegExp[] = [
  /\b(durante\s+el\s+periodo\s+(informado|observado|de\s+\w+\s+a\s+\w+))\b/gi,
  /\b(se\s+registra[n]?\s+[\w\s]+\s+(asistencia|inasistencia|participaci[oó]n|entrega|incumplimiento))\b/gi,
  /\b(el\s+estudiante\s+(asisti[oó]|particip[oó]|entreg[oó]|complet[oó]|registr[oó]|obtuvo))\b/gi,
  /\b(no\s+entreg[oó]\s+\d+\s+de\s+\d+)\b/gi,
  /\b(el\s+promedio\s+(general|parcial|semestral|trimestral)\s+(es|alcanza|se\s+ubica\s+en))\b/gi,
  /\b(se\s+adjunta[n]?\s+registro[s]?\s+de)\b/gi,
  /\b(seg[uú]n\s+(el\s+)?(libro\s+de\s+clases|registro\s+de\s+asistencia|acta\s+de\s+entrevista))\b/gi,
  /\b(se\s+realiz[oó]\s+(entrevista|citaci[oó]n|derivaci[oó]n|seguimiento)\s+el\s+d[ií]a)\b/gi,
];

// ── Simulated AI rewrite suggestions ──
const OBJECTIVE_REWRITES: Record<string, string[]> = {
  "es un irresponsable": ["El estudiante registra incumplimiento reiterado en la entrega de tareas y trabajos durante el periodo informado."],
  "es conflictivo": ["Durante el periodo informado, se han observado dificultades para mantener una interacción adecuada con algunos pares, registrándose [cantidad] de situaciones que interrumpieron el desarrollo normal de la clase."],
  "la mamá nunca apoya": ["No se registra asistencia del apoderado a las citaciones realizadas los días [fechas] durante el periodo informado."],
  "la mamá nunca viene": ["No se registra asistencia del apoderado a las reuniones y citaciones realizadas por el establecimiento durante el periodo [fecha inicio] a [fecha fin]."],
  "es un caso perdido": ["Se han implementado las siguientes medidas de apoyo: [detallar]. Hasta la fecha, los resultados observados son: [resultados]. Se sugiere evaluar las siguientes acciones adicionales: [propuestas]."],
  "se porta mal": ["Durante las clases de [asignatura], se observa que el estudiante [conducta específica: interrumpe con comentarios fuera de contexto / se levanta del puesto sin autorización / no sigue las instrucciones dadas / responde de forma inadecuada cuando se le corrige]."],
  "le cuesta matemáticas": ["En la asignatura de Matemática, el estudiante presenta un rendimiento bajo el promedio del curso. Durante el periodo informado, se registra incumplimiento en la entrega de [X] de [Y] tareas solicitadas. Su promedio parcial se ubica en [nota]."],
  "no participa mucho": ["Se registra participación del estudiante en [X] de [Y] clases durante el periodo informado. Cuando se le solicita directamente, [describe su respuesta: responde adecuadamente / no responde / requiere apoyo adicional]."],
};

function evaluateText(text: string): EvaluatedFragment[] {
  const fragments: EvaluatedFragment[] = [];
  const trimmed = text.trim();
  if (!trimmed) return fragments;

  // Check red rules
  for (const rule of RED_RULES) {
    rule.pattern.lastIndex = 0;
    let match;
    while ((match = rule.pattern.exec(trimmed)) !== null) {
      const matchedText = match[0];
      const suggestionKey = matchedText.toLowerCase().replace(/\s+/g, " ").trim();
      const rewriteOptions = OBJECTIVE_REWRITES[suggestionKey];
      let suggestion = rule.suggestionTemplate;
      if (rewriteOptions && rewriteOptions.length > 0) {
        suggestion = rewriteOptions[0];
      }
      fragments.push({
        text: matchedText,
        level: "rojo",
        reason: rule.reason,
        suggestion,
      });
    }
  }

  // Check yellow rules
  for (const rule of YELLOW_RULES) {
    rule.pattern.lastIndex = 0;
    let match;
    while ((match = rule.pattern.exec(trimmed)) !== null) {
      const matchedText = match[0];
      fragments.push({
        text: matchedText,
        level: "amarillo",
        reason: rule.reason,
        suggestion: rule.suggestionTemplate,
      });
    }
  }

  return fragments;
}

function countGreenPatterns(text: string): number {
  let count = 0;
  for (const pattern of GREEN_PATTERNS) {
    pattern.lastIndex = 0;
    if (pattern.test(text)) count++;
  }
  return count;
}

const LEVEL_CONFIG: Record<EvalLevel, { label: string; bg: string; border: string; text: string; dot: string; icon: string }> = {
  rojo: {
    label: "Lenguaje subjetivo o no respaldado",
    bg: "bg-red-50/80",
    border: "border-red-200/70",
    text: "text-red-700",
    dot: "bg-red-500",
    icon: "ri-error-warning-fill text-red-500",
  },
  amarillo: {
    label: "Frase ambigua o poco específica",
    bg: "bg-amber-50/80",
    border: "border-amber-200/70",
    text: "text-amber-700",
    dot: "bg-amber-500",
    icon: "ri-alert-fill text-amber-500",
  },
  verde: {
    label: "Frase objetiva y útil",
    bg: "bg-emerald-50/80",
    border: "border-emerald-200/70",
    text: "text-emerald-700",
    dot: "bg-emerald-500",
    icon: "ri-check-double-fill text-emerald-500",
  },
};

interface Props {
  text: string;
  className?: string;
  compact?: boolean;
}

const ObjectiveLanguageEvaluator: FC<Props> = ({ text, className = "", compact = false }) => {
  const [showDetails, setShowDetails] = useState(false);

  const fragments = text.trim() ? evaluateText(text) : [];
  const greenCount = countGreenPatterns(text);
  const hasRed = fragments.some((f) => f.level === "rojo");
  const hasYellow = fragments.some((f) => f.level === "amarillo");

  let overallLevel: EvalLevel = "verde";
  if (hasRed) overallLevel = "rojo";
  else if (hasYellow && greenCount < 2) overallLevel = "amarillo";

  const config = LEVEL_CONFIG[overallLevel];

  if (!text.trim()) return null;

  return (
    <div className={`rounded-xl border ${config.border} ${config.bg} overflow-hidden transition-all duration-300 ${className}`}>
      {/* Header bar */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left cursor-pointer transition-colors ${compact ? "" : "hover:bg-black/5"}`}
      >
        <span className={`flex items-center justify-center w-5 h-5 flex-shrink-0`}>
          <i className={`${config.icon} text-sm`} />
        </span>
        <span className={`text-xs font-bold ${config.text} flex-1`}>{config.label}</span>
        {fragments.length > 0 && (
          <span className={`text-[11px] font-bold text-foreground-500`}>
            {fragments.filter((f) => f.level === "rojo").length > 0 && (
              <span className="text-red-500 mr-1.5">{fragments.filter((f) => f.level === "rojo").length}</span>
            )}
            {fragments.filter((f) => f.level === "amarillo").length > 0 && (
              <span className="text-amber-500 mr-1.5">{fragments.filter((f) => f.level === "amarillo").length}</span>
            )}
            {greenCount > 0 && (
              <span className="text-emerald-500">{greenCount}</span>
            )}
          </span>
        )}
        {fragments.length > 0 && (
          <span className="flex items-center justify-center w-4 h-4">
            <i className={`ri-arrow-${showDetails ? "up" : "down"}-s-line text-foreground-400 text-xs`} />
          </span>
        )}
      </button>

      {/* Expandable details */}
      {showDetails && fragments.length > 0 && (
        <div className="px-3.5 pb-3.5 space-y-2 animate-slide-down">
          {fragments.map((fragment, idx) => {
            const fConfig = LEVEL_CONFIG[fragment.level];
            return (
              <div key={idx} className={`rounded-lg border ${fConfig.border} ${fConfig.bg} p-3`}>
                <div className="flex items-start gap-2.5">
                  <span className={`flex items-center justify-center w-3 h-3 rounded-full ${fConfig.dot} flex-shrink-0 mt-0.5`} />
                  <div className="min-w-0 flex-1">
                    <p className={`text-xs font-semibold ${fConfig.text} mb-0.5`}>
                      {fragment.level === "rojo" ? "Frase problemática detectada" : "Frase ambigua detectada"}
                    </p>
                    <p className="text-xs text-foreground-600 mb-1 bg-white/60 rounded-md px-2 py-1 italic border border-background-200/40">
                      &ldquo;{fragment.text}&rdquo;
                    </p>
                    <p className="text-[11px] text-foreground-500 mb-2">{fragment.reason}</p>
                    <div className="bg-white/80 rounded-lg p-2.5 border border-background-200/40">
                      <p className="text-[11px] font-bold text-foreground-400 uppercase tracking-wider mb-1">Sugerencia</p>
                      <p className="text-xs text-foreground-700">{fragment.suggestion}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Helper hook for section evaluation ──
export function useSectionEvaluator() {
  const evaluate = useCallback((text: string): EvaluationResult => {
    const fragments = evaluateText(text);
    const greenCount = countGreenPatterns(text);
    const hasRed = fragments.some((f) => f.level === "rojo");
    const hasYellow = fragments.some((f) => f.level === "amarillo");

    let overallLevel: EvalLevel = "verde";
    if (hasRed) overallLevel = "rojo";
    else if (hasYellow && greenCount < 2) overallLevel = "amarillo";

    let summary = "";
    switch (overallLevel) {
      case "rojo":
        summary = `Se detectaron ${fragments.filter((f) => f.level === "rojo").length} frases con lenguaje subjetivo o no respaldado. Se recomienda revisar y ajustar antes de enviar.`;
        break;
      case "amarillo":
        summary = `Se detectaron ${fragments.filter((f) => f.level === "amarillo").length} frases que requieren mayor precisión. Considere agregar datos específicos.`;
        break;
      case "verde":
        summary = "El texto utiliza lenguaje objetivo y específico. No se detectan problemas de redacción.";
        break;
    }

    return { fragments, overallLevel, summary };
  }, []);

  return { evaluate };
}

export default ObjectiveLanguageEvaluator;
export { evaluateText, LEVEL_CONFIG };